import { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const today = () => new Date().toISOString().split("T")[0];
const companyId = () => Number(localStorage.getItem("companyId") || 0);

const val = (r, ...keys) => {
  for (const k of keys) {
    const x = r?.[k];
    if (x !== undefined && x !== null) return x;
  }
  return "";
};

const safe = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "";
};

const arr = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.result)) return data.result;
  return [];
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) =>
  num(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const errText = (e, fallback) => {
  const d = e?.response?.data;
  if (typeof d === "string" && d.trim()) return d;
  if (typeof d?.message === "string" && d.message.trim()) return d.message;
  if (typeof d?.title === "string" && d.title.trim()) return d.title;
  if (typeof e?.message === "string" && e.message.trim()) return e.message;
  return fallback;
};

const styles = {
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 700 },
  td: { fontSize: 11, padding: "6px 8px", whiteSpace: "nowrap" },
};

const blankForm = () => ({
  refType: "INVOICE",
  refId: "",
  refNo: "",
  refDate: today(),
  partyName: "",
  gstin: "",
  eWayBillNo: "",
  eWayBillDate: today(),
  validTill: "",
  transportMode: "ROAD",
  transporterName: "",
  transporterId: "",
  vehicleNo: "",
  distanceKM: "",
  taxableAmount: "",
  taxAmount: "",
  totalAmount: "",
  notes: "",
});

export default function EWayBillRegisterPage() {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(blankForm());

  const stats = useMemo(() => {
    const active = rows.filter(
      (x) => safe(val(x, "Status", "status")).toUpperCase() === "ACTIVE"
    ).length;

    const cancelled = rows.filter(
      (x) => safe(val(x, "Status", "status")).toUpperCase() === "CANCELLED"
    ).length;

    const totalValue = rows.reduce(
      (s, x) => s + num(val(x, "TotalAmount", "totalAmount")),
      0
    );

    return { active, cancelled, total: rows.length, totalValue };
  }, [rows]);

  useEffect(() => {
    loadRows();
  }, []);

  const loadRows = async () => {
    try {
      setLoading(true);

      const res = await client.get("/eway-bills", {
        params: { companyId: companyId() || undefined },
      });

      setRows(arr(res.data));
    } catch (e) {
      console.error(e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!safe(form.refNo).trim()) return alert("Reference No is required");
    if (!safe(form.eWayBillNo).trim()) return alert("E-Way Bill No is required");
    if (form.transportMode === "ROAD" && !safe(form.vehicleNo).trim())
      return alert("Vehicle No is required for road transport");
    if (form.validTill && form.validTill < form.eWayBillDate)
      return alert("Valid Till date cannot be before E-Way Bill Date");
    if (num(form.distanceKM) < 0) return alert("Distance KM cannot be negative");
    if (num(form.taxableAmount) < 0) return alert("Taxable amount cannot be negative");
    if (num(form.taxAmount) < 0) return alert("Tax amount cannot be negative");
    if (num(form.totalAmount) < 0) return alert("Total amount cannot be negative");

    try {
      setSaving(true);

      await client.post("/eway-bills", {
        companyId: companyId(),
        refType: safe(form.refType),
        refId: form.refId ? Number(form.refId) : null,
        refNo: safe(form.refNo).trim(),
        refDate: form.refDate,
        partyName: safe(form.partyName).trim(),
        gstin: safe(form.gstin).trim().toUpperCase(),
        eWayBillNo: safe(form.eWayBillNo).trim(),
        eWayBillDate: form.eWayBillDate,
        validTill: form.validTill || null,
        transportMode: safe(form.transportMode),
        transporterName: safe(form.transporterName).trim(),
        transporterId: safe(form.transporterId).trim(),
        vehicleNo: safe(form.vehicleNo).trim().toUpperCase(),
        distanceKM: form.distanceKM === "" ? null : num(form.distanceKM),
        taxableAmount: num(form.taxableAmount),
        taxAmount: num(form.taxAmount),
        totalAmount: num(form.totalAmount),
        notes: safe(form.notes).trim(),
      });

      alert("E-Way Bill saved successfully");

      setForm(blankForm());
      await loadRows();
    } catch (e) {
      console.error(e);
      alert(errText(e, "Failed to save E-Way Bill"));
    } finally {
      setSaving(false);
    }
  };

  const cancelBill = async (id) => {
    const reason = prompt("Enter cancellation reason");
    if (!reason || !reason.trim()) return;

    try {
      await client.post(`/eway-bills/cancel/${id}`, {
        companyId: companyId(),
        reason: reason.trim(),
      });

      alert("E-Way Bill cancelled");
      await loadRows();
    } catch (e) {
      console.error(e);
      alert(errText(e, "Failed to cancel E-Way Bill"));
    }
  };

  return (
    <div className="container-fluid py-2" style={{ fontSize: 11, padding: 0 }}>
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 2px 10px rgba(0,0,0,.08)",
        }}
      >
        <div
          style={{
            background:
              "linear-gradient(90deg, rgb(11, 79, 74), rgb(15, 118, 110))",
            color: "#fff",
            padding: "9px 12px",
          }}
        >
          <h6 style={{ fontSize: 13, fontWeight: 800, margin: 0 }}>
            <i className="bi bi-truck-flatbed me-2"></i>
            E-Way Bill Register
          </h6>

          <div style={{ fontSize: 10, opacity: 0.85 }}>
            GST movement register with transporter, vehicle and validity tracking
          </div>
        </div>

        <div style={{ padding: 10 }}>
          <div className="row g-2 mb-2">
            <Stat title="Active" value={stats.active} />
            <Stat title="Cancelled" value={stats.cancelled} />
            <Stat title="Total Bills" value={stats.total} />
            <Stat title="Total Value" value={`₹ ${money(stats.totalValue)}`} />
          </div>

          <Section title="E-Way Bill Entry" />

          <div className="row g-2 mb-2">
            <Field label="Reference Type">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.refType}
                onChange={(e) => setForm({ ...form, refType: e.target.value })}
              >
                <option value="INVOICE">INVOICE</option>
                <option value="DELIVERY_CHALLAN">DELIVERY_CHALLAN</option>
                <option value="PURCHASE_RETURN">PURCHASE_RETURN</option>
                <option value="STOCK_TRANSFER">STOCK_TRANSFER</option>
              </select>
            </Field>

            <Field label="Reference No">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.refNo}
                onChange={(e) => setForm({ ...form, refNo: e.target.value })}
              />
            </Field>

            <Field label="Reference Date">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.refDate}
                onChange={(e) => setForm({ ...form, refDate: e.target.value })}
              />
            </Field>

            <Field label="Party Name" col="col-md-3">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.partyName}
                onChange={(e) => setForm({ ...form, partyName: e.target.value })}
              />
            </Field>

            <Field label="GSTIN">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.gstin}
                onChange={(e) =>
                  setForm({ ...form, gstin: e.target.value.toUpperCase() })
                }
              />
            </Field>

            <Field label="E-Way Bill No">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.eWayBillNo}
                onChange={(e) =>
                  setForm({ ...form, eWayBillNo: e.target.value })
                }
              />
            </Field>

            <Field label="E-Way Date">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.eWayBillDate}
                onChange={(e) =>
                  setForm({ ...form, eWayBillDate: e.target.value })
                }
              />
            </Field>

            <Field label="Valid Till">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.validTill}
                onChange={(e) => setForm({ ...form, validTill: e.target.value })}
              />
            </Field>

            <Field label="Transport Mode">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.transportMode}
                onChange={(e) =>
                  setForm({ ...form, transportMode: e.target.value })
                }
              >
                <option value="ROAD">ROAD</option>
                <option value="RAIL">RAIL</option>
                <option value="AIR">AIR</option>
                <option value="SHIP">SHIP</option>
              </select>
            </Field>

            <Field label="Transporter" col="col-md-3">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.transporterName}
                onChange={(e) =>
                  setForm({ ...form, transporterName: e.target.value })
                }
              />
            </Field>

            <Field label="Transporter ID">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.transporterId}
                onChange={(e) =>
                  setForm({ ...form, transporterId: e.target.value })
                }
              />
            </Field>

            <Field label="Vehicle No">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.vehicleNo}
                onChange={(e) =>
                  setForm({ ...form, vehicleNo: e.target.value.toUpperCase() })
                }
              />
            </Field>

            <Field label="Distance KM">
              <input
                type="number"
                min="0"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.distanceKM}
                onChange={(e) =>
                  setForm({ ...form, distanceKM: e.target.value })
                }
              />
            </Field>

            <Field label="Taxable">
              <input
                type="number"
                min="0"
                step="0.01"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.taxableAmount}
                onChange={(e) =>
                  setForm({ ...form, taxableAmount: e.target.value })
                }
              />
            </Field>

            <Field label="Tax">
              <input
                type="number"
                min="0"
                step="0.01"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.taxAmount}
                onChange={(e) => setForm({ ...form, taxAmount: e.target.value })}
              />
            </Field>

            <Field label="Total">
              <input
                type="number"
                min="0"
                step="0.01"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.totalAmount}
                onChange={(e) => setForm({ ...form, totalAmount: e.target.value })}
              />
            </Field>

            <Field label="Notes" col="col-md-4">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Field>
          </div>

          <div className="d-flex justify-content-end gap-2 mb-3">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              style={styles.btn}
              onClick={() => window.print()}
            >
              Print
            </button>

            <button
              type="button"
              className="btn btn-success btn-sm"
              style={styles.btn}
              disabled={saving || loading}
              onClick={save}
            >
              {saving ? "Saving..." : "Save E-Way Bill"}
            </button>
          </div>

          <Section title="E-Way Bill Register" />

          <div
            style={{
              maxHeight: 500,
              overflow: "auto",
              border: "1px solid #e9ecef",
              borderRadius: 10,
            }}
          >
            <table
              className="table table-bordered table-hover align-middle"
              style={{ fontSize: 11, minWidth: 1600, marginBottom: 0 }}
            >
              <thead>
                <tr>
                  {[
                    "E-Way Bill No",
                    "Date",
                    "Valid Till",
                    "Ref Type",
                    "Ref No",
                    "Party",
                    "GSTIN",
                    "Transporter",
                    "Vehicle",
                    "Distance",
                    "Taxable",
                    "Tax",
                    "Total",
                    "Status",
                    "Action",
                  ].map((h) => (
                    <TH key={h}>{h}</TH>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan="15" className="text-center text-muted" style={{ padding: 18 }}>
                      {loading ? "Loading..." : "No e-way bills found"}
                    </td>
                  </tr>
                ) : (
                  rows.map((r, index) => {
                    const status = safe(val(r, "Status", "status")).toUpperCase();

                    return (
                      <tr key={val(r, "Id", "id") || index}>
                        <td style={{ ...styles.td, fontWeight: 800 }}>
                          {safe(val(r, "EWayBillNo", "eWayBillNo"))}
                        </td>

                        <td style={styles.td}>
                          {safe(val(r, "EWayBillDate", "eWayBillDate")).slice(0, 10)}
                        </td>

                        <td style={styles.td}>
                          {safe(val(r, "ValidTill", "validTill")).slice(0, 10) || "-"}
                        </td>

                        <td style={styles.td}>{safe(val(r, "RefType", "refType"))}</td>

                        <td style={styles.td}>{safe(val(r, "RefNo", "refNo"))}</td>

                        <td style={styles.td}>{safe(val(r, "PartyName", "partyName"))}</td>

                        <td style={styles.td}>{safe(val(r, "GSTIN", "gstin"))}</td>

                        <td style={styles.td}>
                          {safe(val(r, "TransporterName", "transporterName")) || "-"}
                        </td>

                        <td style={styles.td}>
                          {safe(val(r, "VehicleNo", "vehicleNo")) || "-"}
                        </td>

                        <td className="text-end" style={styles.td}>
                          {money(val(r, "DistanceKM", "distanceKM"))}
                        </td>

                        <td className="text-end" style={styles.td}>
                          {money(val(r, "TaxableAmount", "taxableAmount"))}
                        </td>

                        <td className="text-end" style={styles.td}>
                          {money(val(r, "TaxAmount", "taxAmount"))}
                        </td>

                        <td className="text-end" style={styles.td}>
                          {money(val(r, "TotalAmount", "totalAmount"))}
                        </td>

                        <td style={styles.td}>
                          <span
                            className={`badge ${
                              status === "ACTIVE"
                                ? "bg-success"
                                : status === "CANCELLED"
                                ? "bg-danger"
                                : "bg-secondary"
                            }`}
                          >
                            {status || "ACTIVE"}
                          </span>
                        </td>

                        <td style={styles.td}>
                          {status !== "CANCELLED" && (
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              style={styles.btn}
                              onClick={() => cancelBill(val(r, "Id", "id"))}
                            >
                              Cancel
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, col = "col-md-2" }) {
  return (
    <div className={col}>
      <label
        style={{
          fontSize: 10,
          fontWeight: 700,
          marginBottom: 3,
          color: "#495057",
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function Stat({ title, value }) {
  return (
    <div className="col-md-3">
      <div
        style={{
          border: "1px solid #e9ecef",
          background: "#f8fafc",
          borderRadius: 10,
          padding: 9,
          borderTop: "3px solid #0f766e",
        }}
      >
        <div style={{ fontSize: 10, color: "#64748b", fontWeight: 800 }}>
          {title}
        </div>

        <div style={{ fontSize: 15, color: "#0f766e", fontWeight: 900 }}>
          {safe(value)}
        </div>
      </div>
    </div>
  );
}

function Section({ title }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 800,
        color: "#0f766e",
        borderBottom: "1px solid #e9ecef",
        paddingBottom: 4,
        marginBottom: 8,
      }}
    >
      {title}
    </div>
  );
}

function TH({ children }) {
  return (
    <th
      style={{
        fontSize: 10,
        color: "#fff",
        background: "#07111f",
        padding: "7px 8px",
        whiteSpace: "nowrap",
        position: "sticky",
        top: 0,
      }}
    >
      {children}
    </th>
  );
}